import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';

import {
  getStartOfDayLocalFromUTC,
  getStartOfDayUTCFromLocal,
} from 'Helpers/DateTime';

import HelperText from 'Components/UI/Meta/MetaControl/HelperText';
import { usePromptHint } from 'Components/UI/Meta/MetaControl/Types/usePromptHint';
import { getValue } from '../utils';

function Date({
  attrName,
  component,
  metaField,
  onChange,
  onInlineSubmit,
  resource,
  CustomProps,
  SpreadProps,
  HelperProps,
  ...rest
}) {
  const Component = metaField.attr('_component');
  const { label } = usePromptHint(metaField);
  const displayTime = metaField.attr('_displayTime');
  const formatString = metaField.attr('_formatString') || 'll';
  const readonly = metaField.attr('readonly');
  const transformValue = metaField.attr('_transformValue') || null;

  const metaDefaultValue = metaField.attr('defaultValue');

  const emptyValue = '';

  let defaultValue = emptyValue;
  if (metaDefaultValue) {
    defaultValue = metaDefaultValue;
  }

  // If value is not set then it will either be null or the default value from the server if supplied.
  // If value is set then it'll be passed down to the Date component as an ISO string
  const value = getValue(resource, metaField, attrName,
    {
      defaultValue,
      formatValue: (dateString) => {
        if (!dateString) {
          return emptyValue;
        }

        // Given an date string from a date picker, convert to start of day in local time
        return getStartOfDayLocalFromUTC(dateString);
      },
      formatReadOnly: (dateString) => {
        if (!dateString) {
          return emptyValue;
        }

        if (displayTime) {
          return moment(dateString)
            .local()
            .format(formatString);
        }

        // Convert to the start of day in local time and then apply a format string.
        return moment(getStartOfDayLocalFromUTC(dateString)).format(formatString);
      },
    });

  // value is an date formatted to an ISO string
  // onChange is given a date formatted to an ISO string
  const inlineSubmitProps = {};
  if (!readonly) {
    inlineSubmitProps.onBlur = onInlineSubmit;
    inlineSubmitProps.onAccept = onInlineSubmit;
  }

  return (
    <Component
      helperText={
        <HelperText
          metaField={metaField}
          {...HelperProps}
        />
      }
      helperTextValue={HelperProps.helperText}
      readonly={readonly}
      required={metaField.attr('required')}
      hidden={metaField.attr('hidden')}
      label={label}
      value={value} // This should never change here - always update the getValue function above
      onChange={(dateISOString) => {
        // Given an ISO string from a date picker, convert to start of day ISO format in UTC tz
        let val = dateISOString ? getStartOfDayUTCFromLocal(dateISOString) : null;

        // Allow an override to have a chance to manipulate the value before setting it
        if (transformValue) {
          val = transformValue(val);
        }

        onChange(val);
      }}
      {...SpreadProps}
      {...CustomProps}
      {...inlineSubmitProps}
      {...rest}
    />
  );
}

Date.defaultProps = {
  onInlineSubmit: null,
};

Date.propTypes = {
  attrName: PropTypes.string.isRequired,
  metaField: PropTypes.object.isRequired,
  onInlineSubmit: PropTypes.func,
  resource: PropTypes.object.isRequired,
};

export default Date;
